import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, ArrowRight } from "lucide-react";
import { scrollToId } from "@/lib/lenis";

const HERO_IMG =
  "https://images.unsplash.com/photo-1748063578185-3d68121b11ff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBsdXh1cnklMjBhcmNoaXRlY3R1cmUlMjBob3VzZSUyMGV4dGVyaW9yJTIwZHVza3xlbnwwfHx8fDE3OTA4OTE3NjV8MA&ixlib=rb-4.1.0&q=85";

const LINES = ["Stavitelství", "jako umění."];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      <motion.div style={{ y }} className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Moderní rodinný dům za soumraku"
          className="w-full h-[120%] object-cover opacity-45"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/80 via-ink/55 to-ink" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/90 via-ink/30 to-transparent" />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pt-36 pb-28 w-full">
        <motion.div
          initial={{ opacity: 0, y: -14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.7 }}
          className="mb-10"
          data-testid="hero-logo"
        >
          <img
            src="/assets/logo.png"
            alt="RESTART PRO"
            className="h-16 sm:h-20 w-auto object-contain"
          />
          <p className="mt-3 text-xs font-mono tracking-[0.3em] uppercase text-gold/80 leading-relaxed" data-testid="hero-tagline">
            Jsme vaše stavební NE-FIRMA
            <span className="block">Jsme aliance řemeslníků</span>
          </p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.7 }}
          className="inline-flex items-center gap-2 border border-gold/40 bg-gold/10 rounded-full px-4 py-1.5 mb-10"
          data-testid="hero-badge"
        >
          <ShieldCheck size={15} className="text-gold" />
          <span className="text-xs font-mono tracking-[0.18em] uppercase text-gold-light">
            Záruka 60 měsíců na naše práce
          </span>
        </motion.div>

        <h1
          className="font-display text-5xl sm:text-7xl lg:text-8xl leading-[0.95] tracking-tight"
          data-testid="hero-headline"
        >
          {LINES.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-2 -mb-2">
              <motion.span
                className={i === 1 ? "block gold-text" : "block text-zinc-100"}
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.35 + i * 0.18, ease: [0.16, 1, 0.3, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-8 text-lg sm:text-xl text-gold-light/90 font-medium"
          data-testid="hero-subheadline"
        >
          Váš nový dům na klíč za 90 dnů. Bytová jádra za 7 dnů. Komerční fit-outy vždy v
          termínu otevření.
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.8 }}
          className="mt-4 max-w-xl text-zinc-400 leading-relaxed"
          data-testid="hero-subtitle"
        >
          <span className="text-gold-light font-semibold">RESTART PRO</span> — to je odpovědnost
          za výsledek. Nestavíme jen stěny, tvoříme prostor pro život. Komerční fit-outy a
          kanceláře nás naučily naprosté disciplíně — držíme termíny, po každé práci uklízíme a
          hlučné práce vždy naplánujeme jen na dobu, kdy nikoho neruší. Získáte garance stavební
          firmy, ale platíte férové ceny živnostníků.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => scrollToId("kalkulacka")}
            data-testid="hero-cta-calculator"
            className="inline-flex items-center gap-2 bg-gold text-black font-semibold rounded-full px-7 py-3.5 hover:bg-gold-light transition-colors gold-glow"
          >
            Spočítat orientační cenu <ArrowRight size={16} />
          </button>
          <button
            onClick={() => scrollToId("sluzby")}
            data-testid="hero-cta-services"
            className="inline-flex items-center gap-2 border border-white/20 text-zinc-200 rounded-full px-7 py-3.5 hover:border-gold/60 hover:text-gold transition-colors"
          >
            Služby od A do Z
          </button>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-zinc-600"
      >
        <span className="text-[10px] font-mono tracking-[0.3em] uppercase">Scroll</span>
        <span className="w-px h-10 bg-gradient-to-b from-gold/60 to-transparent" />
      </motion.div>
    </section>
  );
}
